// Утилиты для определения толщины труб при генерации

import { DEFAULT_PIPE_COMBOS, isDn25PipeDiameter } from '../pipeCatalog';

/**
 * Получить доступные толщины для заданного диаметра
 * @param {number} diameter - Диаметр трубы
 * @param {string} coating - Покрытие ('Матовый' или 'Зеркало')
 * @returns {string[]} - Массив доступных толщин (например, ['x2', 'x3'])
 */
export function getAvailableThicknesses(diameter, coating = 'Матовый') {
  // ⌀33,7 в каталоге только x3 (зеркало) — для генерации одна допустимая толщина
  if (isDn25PipeDiameter(diameter)) {
    return ['x3'];
  }
  const thicknesses = [];
  
  Object.keys(DEFAULT_PIPE_COMBOS).forEach(key => {
    const [d, thickness, c] = key.split('|');
    if (parseFloat(d) === parseFloat(diameter) && c === coating) {
      thicknesses.push(thickness);
    }
  });
  
  // Сортируем по номеру толщины (x1 < x1.5 < x2 < x3 < x4)
  return thicknesses.sort((a, b) => {
    const numA = parseFloat(a.replace('x', ''));
    const numB = parseFloat(b.replace('x', ''));
    return numA - numB;
  });
}

/**
 * Получить минимальную доступную толщину для диаметра
 * @param {number} diameter - Диаметр трубы
 * @param {string} coating - Покрытие ('Матовый' или 'Зеркало')
 * @returns {string} - Минимальная толщина (например, 'x2')
 */
export function getMinThickness(diameter, coating = 'Матовый') {
  let thicknesses = getAvailableThicknesses(diameter, coating);
  
  // Если нет труб с запрошенным покрытием, пробуем альтернативное
  if (thicknesses.length === 0) {
    const alternativeCoating = coating === 'Матовый' ? 'Зеркало' : 'Матовый';
    thicknesses = getAvailableThicknesses(diameter, alternativeCoating);
  }
  
  return thicknesses.length > 0 ? thicknesses[0] : 'x2'; // По умолчанию x2
}

/**
 * Получить максимальную доступную толщину для диаметра
 * @param {number} diameter - Диаметр трубы
 * @param {string} coating - Покрытие ('Матовый' или 'Зеркало')
 * @returns {string} - Максимальная толщина (например, 'x3')
 */
export function getMaxThickness(diameter, coating = 'Матовый') {
  let thicknesses = getAvailableThicknesses(diameter, coating);
  
  // Если нет труб с запрошенным покрытием, пробуем альтернативное
  if (thicknesses.length === 0) {
    const alternativeCoating = coating === 'Матовый' ? 'Зеркало' : 'Матовый';
    thicknesses = getAvailableThicknesses(diameter, alternativeCoating);
  }
  
  return thicknesses.length > 0 ? thicknesses[thicknesses.length - 1] : 'x2'; // По умолчанию x2
}

/**
 * Проверяет, доступно ли зеркальное покрытие для данного диаметра
 * @param {number} diameter - Диаметр трубы
 * @returns {boolean} - true если зеркальное покрытие доступно
 */
export function hasZerkaloCoating(diameter) {
  const thicknesses = getAvailableThicknesses(diameter, 'Зеркало');
  return thicknesses.length > 0;
}

/**
 * Проверяет, доступно ли матовое покрытие для данного диаметра
 * @param {number} diameter - Диаметр трубы
 * @returns {boolean} - true если матовое покрытие доступно
 */
export function hasMatteCoating(diameter) {
  if (isDn25PipeDiameter(diameter)) {
    return false;
  }
  const thicknesses = getAvailableThicknesses(diameter, 'Матовый');
  return thicknesses.length > 0;
}

/**
 * Получить реальное покрытие для конкретного диаметра и толщины
 * Если запрошенное покрытие недоступно, возвращает альтернативное
 * @param {number} diameter - Диаметр трубы
 * @param {string} thickness - Толщина трубы (например, 'x2')
 * @param {string} preferredCoating - Предпочитаемое покрытие ('Матовый' или 'Зеркало')
 * @returns {string} - Реальное покрытие, которое будет использовано
 */
export function getActualCoating(diameter, thickness, preferredCoating = 'Матовый') {
  if (isDn25PipeDiameter(diameter)) {
    const dn25Z = `33.7|${thickness}|Зеркало`;
    if (DEFAULT_PIPE_COMBOS[dn25Z]) {
      return 'Зеркало';
    }
  }
  // Проверяем, существует ли комбинация с предпочитаемым покрытием
  const key = `${diameter}|${thickness}|${preferredCoating}`;
  if (DEFAULT_PIPE_COMBOS[key]) {
    return preferredCoating;
  }
  
  // Если нет, пробуем альтернативное покрытие
  const alternativeCoating = preferredCoating === 'Матовый' ? 'Зеркало' : 'Матовый';
  const alternativeKey = `${diameter}|${thickness}|${alternativeCoating}`;
  if (DEFAULT_PIPE_COMBOS[alternativeKey]) {
    return alternativeCoating;
  }
  
  // Если ничего не найдено, возвращаем предпочитаемое (будет ошибка, но это крайний случай)
  return preferredCoating;
}

/**
 * Есть ли пара диаметр+толщина в каталоге (любое покрытие).
 */
export function isPipeComboInCatalog(diameter, thickness, coating = 'Матовый') {
  const key = `${diameter}|${thickness}|${coating}`;
  if (DEFAULT_PIPE_COMBOS[key]) {
    return true;
  }
  const alternativeCoating = coating === 'Матовый' ? 'Зеркало' : 'Матовый';
  return Boolean(DEFAULT_PIPE_COMBOS[`${diameter}|${thickness}|${alternativeCoating}`]);
}

/**
 * Толщина для генерации: запрошенная, если есть в каталоге, иначе минимальная по каталогу.
 */
export function resolveCatalogThickness(diameter, requestedThickness, coating = 'Матовый') {
  if (!requestedThickness) {
    return getMinThickness(diameter, coating);
  }
  if (isPipeComboInCatalog(diameter, requestedThickness, coating)) {
    return requestedThickness;
  }
  return getMinThickness(diameter, coating);
}

/**
 * Определяет покрытие для системы труб
 * Правило:
 * - Если у ВСЕХ труб (гидрострелки и коллектора) доступно зеркальное покрытие → используем 'Зеркало'
 * - Если хотя бы у одной трубы доступно только матовое → используем 'Матовый' для всех
 * 
 * @param {number[]} diameters - Массив диаметров всех труб в системе
 * @returns {string} - 'Зеркало' или 'Матовый'
 */
export function determineCoating(diameters) {
  // Проверяем, есть ли зеркальное покрытие у всех диаметров
  const allHaveZerkalo = diameters.every(diameter => hasZerkaloCoating(diameter));
  
  return allHaveZerkalo ? 'Зеркало' : 'Матовый';
}

/**
 * Определить толщину трубы на основе диаметра основной трубы (гидрострелки или коллектора)
 * Правило:
 * - Берём минимальную толщину основной трубы (например, x3 для 219)
 * - Для всех остальных труб используем максимальную доступную толщину,
 *   но НЕ БОЛЬШЕ чем минимальная толщина основной трубы
 * - Если для целевого диаметра нет труб с запрошенным покрытием, используем альтернативное
 * 
 * Примеры:
 * - Основная труба 219 (мин. x3) → все трубы макс. толщиной, но не больше x3
 * - Основная труба 159 (мин. x2) → все трубы макс. толщиной, но не больше x2
 * 
 * @param {number} mainPipeDiameter - Диаметр основной трубы (гидрострелки или коллектора)
 * @param {number} targetDiameter - Диаметр целевой трубы (которую нужно создать)
 * @param {string} coating - Покрытие ('Матовый' или 'Зеркало')
 * @returns {string} - Толщина для использования (например, 'x2', 'x3')
 */
export function determineThickness(mainPipeDiameter, targetDiameter, coating = 'Матовый') {
  // Получаем минимальную толщину основной трубы - это наш лимит
  const mainMinThickness = getMinThickness(mainPipeDiameter, coating);
  const mainMinThicknessNum = parseFloat(mainMinThickness.replace('x', ''));
  
  // Получаем все доступные толщины для целевой трубы
  let availableThicknesses = getAvailableThicknesses(targetDiameter, coating);
  
  // Если нет труб с запрошенным покрытием, пробуем альтернативное
  if (availableThicknesses.length === 0) {
    const alternativeCoating = coating === 'Матовый' ? 'Зеркало' : 'Матовый';
    availableThicknesses = getAvailableThicknesses(targetDiameter, alternativeCoating);
  }
  
  if (availableThicknesses.length === 0) {
    return 'x2'; // По умолчанию
  }
  
  // Фильтруем толщины, которые не превышают лимит основной трубы
  const validThicknesses = availableThicknesses.filter(thickness => {
    const thicknessNum = parseFloat(thickness.replace('x', ''));
    return thicknessNum <= mainMinThicknessNum;
  });
  
  // Если есть подходящие толщины, берём максимальную из них
  if (validThicknesses.length > 0) {
    return validThicknesses[validThicknesses.length - 1]; // Последняя = максимальная
  }
  
  // Если все толщины больше лимита, берём минимальную доступную
  return availableThicknesses[0];
}
